import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { withRouter } from 'react-router'
import get from 'lodash/get'
import Row from '@components/Details/Row'
import PropTypes from 'prop-types'
import { formatNumber, formatDate } from '@atheneum/api/dist/lib'
import DeleteIcon from '@components/Icon/Delete'
import EditIcon from '@components/Icon/Edit'
import ACL from '@components/ACL'
import { showDialog, isCancellationError } from '@components/Dialog'

class InfoPanel extends Component {
  handleContractReferenceClick = contractId => {
    this.props.history.push(`/contract/${contractId}`)
  }

  handleUnlinkActionClick = linkedContract => {
    const { contractStore, growlStore } = this.props
    const { contract } = contractStore
    return showDialog({
      title: 'Unlink this contract?',
      text: linkedContract.contractReference,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Unlink'
    })
      .then(() => {
        return contractStore.unlinkContracts(contract.id, linkedContract.id)
      })
      .catch(err => {
        if (isCancellationError(err)) return
        growlStore.showError(err)
      })
  }

  handleUpdateActionClick = linkedContract => {
    const { contractStore, modalStore } = this.props
    modalStore.show({
      type: 'LINKED_CONTRACT_UPDATE_FORM',
      props: {
        title: 'Update link reason',
        okText: 'Save',
        cancelText: 'Cancel',
        linkedContract,
        contractStore
      }
    })
  }

  render () {
    const { contractStore, staticData, sidebarStore, history } = this.props
    const { contract } = contractStore
    const {
      contractCoverages,
      contractTypes,
      contractTypeDetails
    } = staticData.names
    const { CONTRACT_TYPES } = staticData.CONST

    const contractType = contractTypes[contract.contractTypeId]
    const contractTypeDetail =
      contractTypeDetails[contract.contractTypeDetailId]

    const { parentAccount } = contract

    const clientContactName = contract.clientContact
      ? `${contract.clientContact.title} ${contract.clientContact.fullName}`
      : '-'

    const clientContactHandleClick = contract.clientContact
      ? () => {
          sidebarStore.openSidebar({
            id: contract.clientContact.id,
            template: 'client-contact'
          })
        }
      : null

    const PAYGInfo = (
      <div>
        <Row
          label='Total value'
          text={formatTotalValue(contract, staticData)}
        />
        <Row
          label='Price range'
          text={formatPriceRange(contract, staticData)}
        />
      </div>
    )

    const NonPAYGInfo = (
      <div>
        <Row
          label='Credit amount'
          text={`${formatNumber(contract.creditAmount)} credits`}
        />
        <Row
          label='Credit value'
          text={formatCreditValue(contract, staticData)}
        />
        <Row
          label='Total value'
          text={formatTotalValue(contract, staticData)}
        />
      </div>
    )

    return (
      <div className='contract__group-wrapper'>
        <div className='col-2'>
          <Row
            label='Client'
            text={parentAccount.parentAccountName}
            onClick={() => {
              history.push(`/parent-account/${parentAccount.id}`)
            }}
          />
          <Row
            label='Client key contact'
            text={clientContactName}
            onClick={clientContactHandleClick}
          />
          <Row
            label='Client contract party'
            text={contract.clientContractParty}
          />
          <Row
            label='Atheneum contract party'
            text={get(contract, 'atheneumOffice.entityName', null)}
          />
          <Row
            label='Signed by'
            text={get(contract, 'employeeContact.fullName', null)}
          />
          <Row
            label='Contract coverage'
            text={contractCoverages[contract.contractCoverageId]}
          />
          <ContractCoverageList
            contract={contract}
            staticData={staticData}
            sidebarStore={sidebarStore}
          />
          <Row
            label='Contract type'
            text={`${contractType} ${contractTypeDetail}`}
          />
          <Row label='Signed' text={contract.signed ? 'yes' : 'no'} />
          <LinkedContractsList
            contracts={contract.linkedContracts}
            onLinkClick={this.handleContractReferenceClick}
            onUpdateClick={this.handleUpdateActionClick}
            onUnlinkClick={this.handleUnlinkActionClick}
          />
        </div>
        <div className='col-2 col-2--padding'>
          <Row label='Start – end date' text={formatStartEndDate(contract)} />
          {contract.contractTypeId === CONTRACT_TYPES.PAYG
            ? PAYGInfo
            : NonPAYGInfo}
        </div>
      </div>
    )
  }

  static propTypes = {
    contractStore: PropTypes.object,
    staticData: PropTypes.object,
    sidebarStore: PropTypes.object,
    modalStore: PropTypes.object,
    growlStore: PropTypes.object,
    history: PropTypes.object
  }
}

function formatStartEndDate (contract) {
  const { startDate, endDate } = contract
  return `${formatDate(startDate)} – ${formatDate(endDate)}`
}

function formatPriceRange (contract, staticData) {
  const { currencies } = staticData.names
  const { CONTRACT_TYPES } = staticData.CONST
  const currency = currencies[contract.currencyId]

  if (contract.contractTypeId !== CONTRACT_TYPES.PAYG) return '-'

  return `
  ${formatNumber(contract.rangeMin)}
   – ${formatNumber(contract.rangeMax)}
   ${currency}
  `
}

function formatTotalValue (contract, staticData) {
  const { currencies } = staticData.names
  const currency = currencies[contract.currencyId]
  if (contract.unlimited) return 'unlimited'

  const totalValue = contract.value || contract.calculatedValue
  return `${formatNumber(totalValue)} ${currency}`
}

function formatCreditValue (contract, staticData) {
  const { currencies } = staticData.names
  const currency = currencies[contract.currencyId]

  return `${formatNumber(contract.creditValue)} ${currency}`
}

function ContractCoverageList ({ contract, staticData, sidebarStore }) {
  const { CONTRACT_COVERAGES } = staticData.CONST

  let items
  let nameProp
  let sidebarTemplate

  if (contract.contractCoverageId === CONTRACT_COVERAGES.GLOBAL) {
    return null
  }

  if (contract.contractCoverageId === CONTRACT_COVERAGES.SPECIFIC_ACCOUNTS) {
    items = contract.accounts
    nameProp = 'companyName'
    sidebarTemplate = 'client-account'
  }

  if (contract.contractCoverageId === CONTRACT_COVERAGES.SPECIFIC_PROJECTS) {
    items = contract.projects
    nameProp = 'projectName'
    sidebarTemplate = 'project'
  }

  return items.map(item => (
    <Row
      key={item.id}
      text={item[nameProp]}
      onClick={() => {
        sidebarStore.openSidebar({
          id: item.id,
          template: sidebarTemplate
        })
      }}
    />
  ))
}

ContractCoverageList.propTypes = {
  contract: PropTypes.object,
  staticData: PropTypes.object,
  sidebarStore: PropTypes.object
}

const LinkedContractsList = observer(
  ({ contracts, onLinkClick, onUnlinkClick, onUpdateClick }) => {
    return (
      <div className='row'>
        <span className='row__name'>Linked contracts:</span>
        {contracts.length ? (
          <div className='row__value row--link'>
            {contracts.map(linkedContract => {
              return (
                <div className='linked-contract__wrapper'>
                  <div
                    className='linked-contract__reference'
                    onClick={() => onLinkClick(linkedContract.id)}
                  >
                    {linkedContract.contractReference}
                  </div>
                  <div className='linked-contract__link-reason'>
                    {linkedContract.contractToContract.linkReason}
                  </div>
                  <ACL roles={['AdvancedReporting', 'Admin']}>
                    <div className='linked-contract__actions-wrapper'>
                      <span
                        className='linked-contract__action'
                        onClick={() => onUpdateClick(linkedContract)}
                      >
                        <EditIcon />
                      </span>
                      <span
                        className='linked-contract__action'
                        onClick={() => onUnlinkClick(linkedContract)}
                      >
                        <DeleteIcon />
                      </span>
                    </div>
                  </ACL>
                </div>
              )
            })}
          </div>
        ) : (
          '-'
        )}
      </div>
    )
  }
)

LinkedContractsList.propTypes = {
  contracts: PropTypes.array,
  onlinkClick: PropTypes.func,
  onUpdateClick: PropTypes.func,
  onUnlinkClick: PropTypes.func
}

ContractCoverageList.propTypes = {
  contract: PropTypes.object,
  staticData: PropTypes.object,
  sidebarStore: PropTypes.object
}

export default withRouter(
  inject(
    'staticData',
    'sidebarStore',
    'modalStore',
    'growlStore'
  )(observer(InfoPanel))
)
